import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ChevronDown, ListTodo, LogOut, RefreshCw, Settings, UserRound } from 'lucide-react';
import { reloadApp } from '@/lib/app-update';
import { useAuth } from '@/hooks/use-auth';
import { useHideOnScrollDown } from '@/hooks/useHideOnScrollDown';
import { usePermission } from '@/hooks/usePermission';
import { switchActiveTeam } from '@/utils/switchTeam';
import { confirmDiscardUnsavedChanges, hasUnsavedChanges } from '@/utils/unsavedChanges';
import { hasStoredDrafts } from '@/lib/ui-prefs';
import { confirmDialog } from '@/components/ConfirmDialog';
import { getPageTitle } from '@/navigation/nav-items';
import { hasPermission as roleHasPermission, type Permission, type Role } from '@shared/permissions';
import { usePageTopBarOptional } from '@/components/layout/PageTopBarContext';
import { PageBreadcrumbs } from '@/components/PageBreadcrumbs';
import { MemberAvatar, StoreAvatar } from '@/components/team/MemberAvatar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { GlobalSearch } from '@/components/layout/omni-search/GlobalSearch';
import type { OmniEntityKind } from '@/components/layout/omni-search/types';
import { buildOmniNavSections } from '@/components/layout/omni-search/build-nav-sections';
import { useProductContentStore } from '@/stores/productContentStore';
import { cn } from '@/lib/utils';
import { NotificationBell } from '@/components/tasks/NotificationBell';
import { LiveUpdatesIndicator } from '@/components/LiveUpdatesIndicator';
import { MemberProfileDialog } from '@/components/team/MemberProfileDialog';
import { Spinner } from '@/components/ui/spinner';
import { useReportFeatures } from '@/reports/use-report-features';

type StoreMenuItemsProps = {
  currentStoreName: string;
  otherTeams: { id: string; displayname: string }[];
  isSwitchingTeam: boolean;
  onTeamSelect: (teamId: string, teamName: string) => void;
};

function StoreMenuItems({
  currentStoreName,
  otherTeams,
  isSwitchingTeam,
  onTeamSelect,
}: StoreMenuItemsProps) {
  return (
    <>
      <DropdownMenuLabel>Stores</DropdownMenuLabel>
      <DropdownMenuItem disabled>
        <StoreAvatar name={currentStoreName} size="sm" />
        <span className="truncate">{currentStoreName}</span>
        <Check className="ml-auto size-4" />
      </DropdownMenuItem>
      {otherTeams.map((team) => (
        <DropdownMenuItem
          key={team.id}
          disabled={isSwitchingTeam}
          onClick={() => onTeamSelect(team.id, team.displayname)}
        >
          <StoreAvatar name={team.displayname} size="sm" />
          <span className="truncate">{team.displayname}</span>
        </DropdownMenuItem>
      ))}
    </>
  );
}

/**
 * Slim sticky bar across the top of every authenticated page: breadcrumb trail on
 * the left, store/user switcher on the right. Page actions (Edit, Export, etc.)
 * belong in the page body — never here. Global status indicators (e.g. product
 * content queue) are an intentional exception.
 */
export function AppTopBar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const topBar = usePageTopBarOptional();
  const breadcrumbs = topBar?.breadcrumbs ?? null;
  const [isSwitchingTeam, setIsSwitchingTeam] = useState(false);
  const [switchingToName, setSwitchingToName] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const topBarHidden = useHideOnScrollDown(location.pathname);

  useEffect(() => {
    const shell = headerRef.current?.closest<HTMLElement>('[data-slot="app-shell"]');
    shell?.style.setProperty(
      '--app-topbar-height',
      topBarHidden ? '0px' : '3rem'
    );
  }, [topBarHidden]);

  const canViewTeam = usePermission('TEAM_MEMBER_MANAGE');
  const canSyncSetup = usePermission('SYNC_SETUP');
  const canViewProductContent = usePermission('PRODUCT_CONTENT_VIEW');
  const activeChangeCount = useProductContentStore((s) => s.activeChangeCount);
  const changeSetStatus = useProductContentStore((s) => s.changeSet?.status);
  const showProductContentQueue =
    canViewProductContent &&
    (activeChangeCount > 0 ||
      changeSetStatus === 'committing' ||
      changeSetStatus === 'failed');
  const isTeamActive = user?.active_team?.status === 'active';
  const hasActiveTeam = Boolean(user?.active_team?.id);
  const userRole = (user?.role as Role | undefined) ?? null;

  const hasPermission = (permission: Permission) => {
    if (!userRole) return false;
    return roleHasPermission(userRole, permission);
  };

  const reportFeatures = useReportFeatures();
  const omniSections = useMemo(
    () =>
      buildOmniNavSections({
        isTeamActive: Boolean(isTeamActive),
        hasPermission,
        reportFeatures,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasPermission closes over userRole
    [isTeamActive, userRole, reportFeatures]
  );

  const canViewTasks = usePermission('TASKS');
  const canViewPolicies = usePermission('POLICIES');
  const canIncludeOmniEntity = useCallback(
    (kind: OmniEntityKind) =>
      isTeamActive === true && (kind !== 'tasks' || canViewTasks) && (kind !== 'policies' || canViewPolicies),
    [isTeamActive, canViewTasks, canViewPolicies]
  );

  const fallbackTitle = getPageTitle(location.pathname);
  const currentStoreName = user?.active_team?.displayname || 'Store';
  const userName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const otherTeams =
    user?.teams?.filter((team) => team.id !== user?.active_team?.id) ?? [];
  const hasMultipleTeams = (user?.teams?.length ?? 0) > 1;

  const showSettingsMenu =
    (isTeamActive && canSyncSetup) || canViewTeam;

  // Signing out throws away drafts kept in this browser (decision 31 a), so unsaved work asks
  // first: this page's, and drafts other pages left stored (bulk edits, inbox replies).
  const handleLogout = async () => {
    if (hasUnsavedChanges() || hasStoredDrafts()) {
      const ok = await confirmDialog({
        title: 'Log out?',
        description: 'Unsaved changes and drafts kept in this browser (bulk edits, email replies) will be lost.',
        confirmLabel: 'Discard and log out',
        destructive: true,
      });
      if (!ok) return;
    }
    await logout();
  };

  const handleTeamSelect = async (teamId: string, teamName: string) => {
    const discard = await confirmDiscardUnsavedChanges((message) =>
      confirmDialog({ title: 'Switch team?', description: message, confirmLabel: 'Discard and switch', destructive: true })
    );
    if (!discard) return;
    setSwitchingToName(teamName);
    setIsSwitchingTeam(true);
    try {
      await switchActiveTeam(teamId);
    } catch (error) {
      console.error('Error switching team:', error);
      toast.error(`Could not switch to ${teamName}. Try again.`);
      setIsSwitchingTeam(false);
      setSwitchingToName(null);
    }
  };

  return (
    <>
      {isSwitchingTeam && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="fixed inset-0 z-[10000] flex flex-col items-center justify-center gap-4 bg-background/90"
        >
          <Spinner size="xl" className="text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            {switchingToName
              ? `Switching to ${switchingToName}…`
              : 'Switching store…'}
          </p>
        </div>
      )}

      <header
        ref={headerRef}
        className={cn(
          'sticky top-0 z-40 flex h-12 shrink-0 items-center gap-2 border-b bg-background px-4 transition-transform duration-200 ease-out',
          topBarHidden && '-translate-y-full pointer-events-none'
        )}
      >
        <SidebarTrigger className="-ml-1 md:hidden" />
        <Separator orientation="vertical" className="mr-1 h-4 md:hidden" />
        <div className="min-w-0 flex-1 overflow-hidden">
          {breadcrumbs ?? (
            <PageBreadcrumbs items={[{ label: fallbackTitle }]} />
          )}
        </div>

        {/* Phones: search is just its icon, so the buttons on the right never run into it. */}
        {hasActiveTeam && (
          <div className="flex w-auto shrink-0 justify-center sm:w-48">
            <GlobalSearch
              sections={omniSections}
              canIncludeEntity={canIncludeOmniEntity}
            />
          </div>
        )}

        {/* Equal flex either side keeps search centred when there's room. These buttons never
            shrink below what they need (so they can't slide under search at in-between widths);
            the breadcrumbs shrink instead. */}
        <div className="flex flex-1 shrink-0 items-center justify-end gap-1 max-sm:flex-none">
          {showProductContentQueue ? (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="gap-1.5 border-warning/40 bg-warning/10 px-2 text-warning-strong hover:bg-warning/15"
            >
              <Link
                to="/product-content-queue"
                aria-label={
                  activeChangeCount > 0
                    ? `${activeChangeCount} queued content updates`
                    : 'Content queue'
                }
                data-hint="Queued content updates"
              >
                <ListTodo className="size-3.5 shrink-0" aria-hidden />
                <span className="tabular-nums">
                  {changeSetStatus === 'committing'
                    ? 'Committing…'
                    : changeSetStatus === 'failed'
                      ? 'Failed'
                      : activeChangeCount}
                </span>
              </Link>
            </Button>
          ) : null}

          {hasMultipleTeams ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isSwitchingTeam}
                  // Below lg the store is still in the account menu; the breadcrumbs need the room.
                  className="hidden max-w-48 gap-1 px-2 text-sm font-normal text-muted-foreground hover:text-foreground lg:inline-flex"
                  data-hint={currentStoreName}
                  aria-label={`Switch store (current: ${currentStoreName})`}
                >
                  <span className="truncate">{currentStoreName}</span>
                  <ChevronDown className="size-3.5 shrink-0 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-56">
                <StoreMenuItems
                  currentStoreName={currentStoreName}
                  otherTeams={otherTeams}
                  isSwitchingTeam={isSwitchingTeam}
                  onTeamSelect={handleTeamSelect}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span
              className="hidden max-w-48 truncate text-sm text-muted-foreground lg:inline"
              data-hint={currentStoreName}
            >
              {currentStoreName}
            </span>
          )}

          {isTeamActive ? <LiveUpdatesIndicator /> : null}
          {isTeamActive ? <NotificationBell /> : null}

          {showSettingsMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Settings"
                >
                  <Settings />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-48">
                <DropdownMenuLabel>Settings</DropdownMenuLabel>
                {isTeamActive && canSyncSetup && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/integrations">Integrations</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/marketing/settings">Marketing settings</Link>
                    </DropdownMenuItem>
                  </>
                )}
                {canViewTeam && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/team">Team</Link>
                    </DropdownMenuItem>
                    {isTeamActive && (
                      <DropdownMenuItem asChild>
                        <Link to="/team-settings">Team Settings</Link>
                      </DropdownMenuItem>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="rounded-full"
                disabled={isSwitchingTeam}
                aria-label="Account and stores"
              >
                <MemberAvatar name={userName} photourl={user?.photoURL} size="sm" hint={false} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56">
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <MemberAvatar name={userName} photourl={user?.photoURL} size="sm" hint={false} />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{userName}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user?.email || ''}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              {hasActiveTeam ? (
                <DropdownMenuItem onSelect={() => setProfileOpen(true)}>
                  <UserRound />
                  Profile
                </DropdownMenuItem>
              ) : null}

              <DropdownMenuSeparator />
              <StoreMenuItems
                currentStoreName={currentStoreName}
                otherTeams={otherTeams}
                isSwitchingTeam={isSwitchingTeam}
                onTeamSelect={handleTeamSelect}
              />

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => reloadApp()}>
                <RefreshCw />
                Reload app
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => void handleLogout()}>
                <LogOut />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {user?.id ? (
            <MemberProfileDialog userId={user.id} isMe open={profileOpen} onOpenChange={setProfileOpen} />
          ) : null}
        </div>
      </header>
    </>
  );
}
